export const PIPELINE_STATUS = {
  PENDING_HR: 'Pending HR',
  PENDING_OPS: 'Pending Ops',
  PENDING_TL: 'Pending TL',
  PENDING_CLIENT: 'Pending Client',
  PENDING_HEAD_ADMIN: 'Pending Head Admin',
  HIRED: 'Hired',
  FAILED_INTERVIEW: 'Failed Interview',
  PASSED_INTERVIEW: 'Passed Interview'
};

export const isFailedInterviewStatus = (status) => {
  const normalized = (status || '').trim().toLowerCase();
  return /failed\s*interview/.test(normalized) || normalized === 'rejected';
};

export const isPassedInterviewStatus = (status) => {
  const normalized = (status || '').trim().toLowerCase();
  return /passed\s*interview/.test(normalized);
};

export const isReviewQueueApplicant = (app) => {
  const normalized = (app?.status || '').trim().toLowerCase();
  if (normalized === PIPELINE_STATUS.HIRED.toLowerCase()) return false;
  if (isFailedInterviewStatus(app?.status)) return false;
  if (isPassedInterviewStatus(app?.status)) return false;
  return true;
};

const STAGE_ORDER = [
  PIPELINE_STATUS.PENDING_HR,
  PIPELINE_STATUS.PENDING_TL,
  PIPELINE_STATUS.PENDING_OPS,
  PIPELINE_STATUS.PENDING_CLIENT,
  PIPELINE_STATUS.PENDING_HEAD_ADMIN,
  PIPELINE_STATUS.HIRED
];

export const isSharedQueueDepartment = (departmentKey) => (
  departmentKey === 'TL' || departmentKey === 'OPERATIONS'
);

export const usesInterviewerAssignment = (departmentKey) => (
  !isSharedQueueDepartment(departmentKey)
);

export const getDepartmentKeyForStatus = (status) => {
  const normalized = (status || '').trim();
  const map = {
    [PIPELINE_STATUS.PENDING_HR]: 'HR',
    [PIPELINE_STATUS.PENDING_TL]: 'TL',
    [PIPELINE_STATUS.PENDING_OPS]: 'OPERATIONS',
    [PIPELINE_STATUS.PENDING_CLIENT]: 'CLIENT',
    [PIPELINE_STATUS.PENDING_HEAD_ADMIN]: 'HEAD_ADMIN',
  };
  return map[normalized] || null;
};

export const getDepartmentKey = (user) => {
  const position = (user?.position || '').toUpperCase();
  const roleName = (user?.role || '').toUpperCase();

  if (roleName === 'HEAD ADMIN' || position === 'HEAD ADMIN') return 'HEAD_ADMIN';
  if (position.includes('HR') || roleName === 'HR') return 'HR';
  if (position.includes('OPER') || roleName === 'OPERATIONS') return 'OPERATIONS';
  if (position.includes('TEAM LEADER') || position.includes('TEAM LEAD') || roleName === 'TL' || roleName === 'TEAM LEADER') {
    return 'TL';
  }
  if (position.includes('CLIENT') || roleName === 'CLIENT') return 'CLIENT';

  return roleName;
};

export const getRequiredStatusForDepartment = (departmentKey) => {
  const map = {
    HR: PIPELINE_STATUS.PENDING_HR,
    OPERATIONS: PIPELINE_STATUS.PENDING_OPS,
    TL: PIPELINE_STATUS.PENDING_TL,
    CLIENT: PIPELINE_STATUS.PENDING_CLIENT,
    HEAD_ADMIN: PIPELINE_STATUS.PENDING_HEAD_ADMIN
  };
  return map[departmentKey] || null;
};

export const getNextStatusOnApprove = (departmentKey) => {
  const map = {
    HR: PIPELINE_STATUS.PENDING_TL,
    TL: PIPELINE_STATUS.PENDING_OPS,
    OPERATIONS: PIPELINE_STATUS.PENDING_CLIENT,
    CLIENT: PIPELINE_STATUS.PASSED_INTERVIEW,
    HEAD_ADMIN: PIPELINE_STATUS.HIRED
  };
  return map[departmentKey] || null;
};

export const isTerminalPipelineStatus = (status) => {
  const normalized = (status || '').trim();
  return (
    normalized === PIPELINE_STATUS.HIRED ||
    isFailedInterviewStatus(normalized) ||
    /^\s*(on\s*hold|onhold)\s*$/i.test(normalized) ||
    /^\s*application\s*retracted\s*$/i.test(normalized)
  );
};

export const canDepartmentReviewApplicant = (app, user, { isHeadAdmin = false } = {}) => {
  const status = (app?.status || '').trim();
  if (isTerminalPipelineStatus(status)) return false;

  if (isHeadAdmin) {
    return status === PIPELINE_STATUS.PENDING_HEAD_ADMIN;
  }

  const departmentKey = getDepartmentKey(user);
  const requiredStatus = getRequiredStatusForDepartment(departmentKey);
  return Boolean(requiredStatus && status === requiredStatus);
};

export const getApproveTargetStatus = (user, { isHeadAdmin = false } = {}) => {
  if (isHeadAdmin) return PIPELINE_STATUS.HIRED;
  return getNextStatusOnApprove(getDepartmentKey(user));
};

export const getPipelineStageLabel = (status, labels = {}) => {
  const normalized = (status || '').trim();
  const map = {
    [PIPELINE_STATUS.PENDING_HR]: labels.hr || 'HR',
    [PIPELINE_STATUS.PENDING_OPS]: labels.operations || 'Operations',
    [PIPELINE_STATUS.PENDING_TL]: labels.teamLeader || 'Team Leader',
    [PIPELINE_STATUS.PENDING_CLIENT]: labels.client || 'Client',
    [PIPELINE_STATUS.PENDING_HEAD_ADMIN]: labels.headAdmin || 'Head Admin'
  };
  return map[normalized] || normalized;
};

export const getPipelineWaitingMessage = (app, user, labels = {}, { isHeadAdmin = false } = {}) => {
  const status = (app?.status || '').trim();

  if (isFailedInterviewStatus(status)) {
    return labels.failedInterview || 'Applicant failed the interview and will not proceed further.';
  }

  if (isHeadAdmin) {
    if (status === PIPELINE_STATUS.PENDING_HEAD_ADMIN) return '';
    return labels.headAdminWaiting || 'Applicant is still in the department review pipeline.';
  }

  const departmentKey = getDepartmentKey(user);
  const requiredStatus = getRequiredStatusForDepartment(departmentKey);
  if (!requiredStatus || status === requiredStatus) return '';

  const requiredIndex = STAGE_ORDER.indexOf(requiredStatus);
  const currentIndex = STAGE_ORDER.indexOf(status);

  if (currentIndex >= 0 && requiredIndex >= 0 && currentIndex < requiredIndex) {
    const waitingFor = getPipelineStageLabel(status, labels);
    return (labels.waitingFor || 'Waiting for {stage} approval before you can review.')
      .replace('{stage}', waitingFor);
  }

  if (currentIndex > requiredIndex) {
    return labels.alreadyPassed || 'This applicant has already passed your review stage.';
  }

  return labels.notYourTurn || 'Not yet your turn to review this applicant.';
};

export const parseReviewHistory = (appOrHistory) => {
  const raw = appOrHistory && typeof appOrHistory === 'object' && 'review_history' in (appOrHistory || {})
    ? appOrHistory.review_history
    : appOrHistory;

  if (!raw) return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
};

export const getStageReviewer = (app, departmentKey) => {
  if (!departmentKey) return null;
  const entry = parseReviewHistory(app)[departmentKey];
  return entry && typeof entry === 'object' ? entry : null;
};

export const getFailedInterviewReviewer = (app) => {
  const history = parseReviewHistory(app);
  const priority = ['HEAD_ADMIN', 'CLIENT', 'TL', 'OPERATIONS', 'HR'];

  for (const department of priority) {
    const entry = history[department];
    if (entry && isFailedInterviewStatus(entry.action)) {
      return { ...entry, department };
    }
  }

  for (const [department, entry] of Object.entries(history)) {
    if (entry && typeof entry === 'object' && isFailedInterviewStatus(entry.action)) {
      return { ...entry, department };
    }
  }

  return null;
};

const FAILED_RETURN_STATUS_BY_DEPARTMENT = {
  HR: PIPELINE_STATUS.PENDING_HR,
  OPERATIONS: PIPELINE_STATUS.PENDING_OPS,
  TL: PIPELINE_STATUS.PENDING_TL,
  CLIENT: PIPELINE_STATUS.PENDING_CLIENT,
  HEAD_ADMIN: PIPELINE_STATUS.PENDING_HEAD_ADMIN,
};

/** Status to restore when returning a failed applicant back to Applications for Review. */
export const getFailedInterviewReturnStatus = (app) => {
  const failedReviewer = getFailedInterviewReviewer(app);
  if (!failedReviewer) return null;

  const savedStatus = (failedReviewer.failedFromStatus || failedReviewer.failed_from_status || '').trim();
  if (savedStatus && STAGE_ORDER.includes(savedStatus)) {
    return savedStatus;
  }

  return FAILED_RETURN_STATUS_BY_DEPARTMENT[failedReviewer.department] || null;
};

export const canReturnFailedInterviewToReview = (app) => Boolean(getFailedInterviewReturnStatus(app));

export const getCurrentStageReviewer = (app, user) => (
  getStageReviewer(app, getDepartmentKey(user))
);

export const isSameReviewer = (entry, user) => {
  if (!entry || !user) return false;
  const entryId = entry.userId != null ? String(entry.userId) : '';
  const userId = user.id != null ? String(user.id) : '';
  if (entryId && userId && entryId === userId) return true;

  const entryEmail = String(entry.email || '').trim().toLowerCase();
  const userEmail = String(user.email || '').trim().toLowerCase();
  return Boolean(entryEmail && userEmail && entryEmail === userEmail);
};

/** Apps become visible to Ops/TL/Client only after they pass HR (reach that stage). */
export const canDepartmentSeeApplication = (app, user, { isHeadAdmin = false } = {}) => {
  if (isHeadAdmin) return true;
  if (!app) return false;

  const departmentKey = getDepartmentKey(user);
  const requiredStatus = getRequiredStatusForDepartment(departmentKey);
  if (!requiredStatus) return false;

  const status = (app.status || '').trim();
  const history = parseReviewHistory(app);
  const requiredIndex = STAGE_ORDER.indexOf(requiredStatus);

  if (isFailedInterviewStatus(status)) {
    if (history[departmentKey]) return true;
    // Failed at/before this stage: only earlier departments that touched it, or HR.
    return departmentKey === 'HR';
  }

  const currentIndex = STAGE_ORDER.indexOf(status);
  if (currentIndex < 0) {
    // Non-pipeline statuses (Onhold, Pooling, etc.): visible if this dept already reviewed.
    return Boolean(history[departmentKey]);
  }

  // Reached or passed this department's stage in the pipeline.
  return currentIndex >= requiredIndex;
};

export const buildReviewerPayload = (user) => {
  if (!user) {
    return {
      reviewer_name: '',
      reviewer_email: '',
      reviewer_id: null,
      reviewer_department: '',
    };
  }

  return {
    reviewer_name: user.full_name || user.name || user.email || 'Staff',
    reviewer_email: user.email || '',
    reviewer_id: user.id ?? null,
    reviewer_department: getDepartmentKey(user) || '',
  };
};
